import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { getMyDashboardsQuery, saveMyDashboardsMutation } from '@src/util/query/dashboardLayout.query';

/**
 * The dashboards the signed-in user built, next to the standard Operations / Compliance ones
 * (those stay as they are and aren't stored here). Every change shows at once and the whole set is
 * saved on the server shortly after (saveMyDashboards), so it follows them to any browser.
 */
export const useMyDashboards = () => {
  const { data, loading } = useQuery(getMyDashboardsQuery, { fetchPolicy: 'network-only' });
  const [save] = useMutation(saveMyDashboardsMutation);
  const [local, setLocal] = useState(null);
  const [status, setStatus] = useState('idle');
  const timer = useRef(null);
  const pending = useRef(null);

  const dashboards = local || data?.getMyDashboards?.response?.dashboards || [];

  const flush = useCallback(async () => {
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setStatus('saving');
    try {
      await save({ variables: { dashboards: next } });
      setStatus(pending.current ? 'saving' : 'saved');
    } catch (err) {
      console.error('[dashboards] save failed:', err?.message);
      setStatus('error');
    }
  }, [save]);

  // a burst of changes (dragging, stretching) is one save
  const commit = useCallback((next) => {
    setLocal(next);
    pending.current = next;
    setStatus('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 600);
  }, [flush]);

  useEffect(() => () => { clearTimeout(timer.current); flush(); }, [flush]);

  const updateDashboard = useCallback((id, change) => {
    commit(dashboards.map((d) => (d.id === id ? { ...d, ...(typeof change === 'function' ? change(d) : change) } : d)));
  }, [dashboards, commit]);

  return { loading: loading && !data, dashboards, status, commit, updateDashboard };
};
